import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react"
import { flushSync } from "react-dom"
import { Global } from "@emotion/react"
import { createStore } from "zustand/vanilla"
import { useStore } from "zustand"
import Moveable, {
  OnDrag, OnDragEnd, OnDragStart, OnResize, OnResizeEnd, OnResizeStart, OnRotate, OnRotateEnd,
  OnRotateStart,
} from "react-moveable"
import { getGridPositions } from "./lib/geometry"
import { Rect, Size } from "./lib/viewport"
import {
  useContentScale, useGetViewport, useSelectedIds, useZoom,
} from "./Editor"
import useOneDistancePerGap from "./useOneDistancePerGap"

/** Snapping starts within this many screen pixels of a guide. */
const SNAP_THRESHOLD_PX = 4
const ROTATION_STEP = 15

// Moveable marks the page with the gesture in progress; its cursor holds wherever the pointer
// goes. Resizing and rotating take Moveable's own cursors, over this one
const gestureCursorStyles = {
  ":where(body.moveable-view-dragging)": { cursor: "move" },
  "body.moveable-view-dragging *": { cursor: "inherit !important" },
}

export type LayerObject = Rect & {
  id: string
  rotation: number
  /** May be moved and resized past the bounds; other objects are kept inside. */
  canLeaveBounds: boolean
}

/** What a gesture changed on one object, in content units. */
export type ObjectChange = Partial<Rect> & { rotation?: number }

/** How an object may be transformed, declared by its parts. */
export type ObjectRules = {
  resizable: boolean
  /** Always square. */
  square: boolean
  minSize: number
  rotatable: boolean
  /** No moving or resizing (while its text is edited, for instance). */
  locked: boolean
  /** Whether a press at a point lands on the object; outside, it goes to what's behind. */
  hitTest?: (point: { clientX: number; clientY: number; currentTarget: Element }) => boolean
}

const DEFAULT_RULES: ObjectRules = {
  resizable: false, square: false, minSize: 1, rotatable: false, locked: false,
}

type LiveObject = { rect: Rect; rotation: number }

/** While objects are moved, resized or rotated: where they are, for their labels and bodies. */
type LayerState = { live: Record<string, LiveObject> }

type LayerContextValue = {
  register: (id: string, element: HTMLElement | null) => void
  setRules: (id: string, rules: Partial<ObjectRules>) => void
  /** Hands a press on an object that's being selected to the gesture, once it's selected. */
  dragAfterSelect: (event: MouseEvent) => void
  /** Hands a press on a selected object to the gesture (a group doesn't take it by itself). */
  drag: (event: MouseEvent) => void
  store: ReturnType<typeof createLayerStore>
  bounds: Size
  scale: number
}

const createLayerStore = () => createStore<LayerState>()(() => ({ live: {} }))

const LayerContext = createContext<LayerContextValue | null>(null)

// Objects by their drawn element, so the canvas can ask whether a press hits one
const rulesByElement = new WeakMap<Element, ObjectRules>()

/** Whether a press on an object misses it (inside an unfilled shape): the canvas takes it then. */
export const pressMissesObject = (event: MouseEvent) => {
  const element = (event.target as Element).closest?.("[data-object-id]")
  const hitTest = element && rulesByElement.get(element)?.hitTest

  return !!element && !!hitTest && !hitTest({
    clientX: event.clientX, clientY: event.clientY, currentTarget: element,
  })
}

type Target = HTMLElement | SVGElement
type Entry = readonly [string, ObjectChange]
type ResizeStart = Rect & { drawnWidth: number; drawnHeight: number }

const round = (value: number, digits = 3) => Number(value.toFixed(digits))
const normaliseAngle = (degrees: number) => round(degrees % 360, 2)

type Props = {
  objects: LayerObject[]
  /** The area objects live in, in content units. */
  bounds: Size
  /** Snap to the bounds, the other objects and the grid guides. */
  snapping?: boolean
  /** Also snap to the bounds' thirds and centre. */
  gridGuides: boolean
  /** Writes a distance in content units, for the guides' distance labels. */
  formatDistance?: (distance: number) => string
  /** Commits a gesture: every object it changed, as one step. */
  onTransform: (changes: Map<string, ObjectChange>) => void
  children: React.ReactNode
}

/**
 * Holds the objects' shared interactions, through Moveable: moving, resizing and rotating the
 * selection, together when there are several, kept inside the bounds unless every object may
 * leave them, snapping to the bounds, the grid and the other objects. During a gesture the
 * elements move on screen only; the change is committed once, on release.
 */
const ObjectLayer = ({
  objects,
  bounds,
  snapping = true,
  gridGuides,
  formatDistance,
  onTransform,
  children,
}: Props) => {
  const zoom = useZoom()
  const scale = useContentScale()
  const selectedIds = useSelectedIds()
  const getViewport = useGetViewport()
  const [store] = useState(createLayerStore)
  const moveableRef = useRef<Moveable>(null)
  useOneDistancePerGap(moveableRef)
  const elements = useRef(new Map<string, HTMLElement>())
  const rules = useRef(new Map<string, ObjectRules>())
  const [registryVersion, setRegistryVersion] = useState(0)
  const [shiftHeld, setShiftHeld] = useState(false)

  const latest = useRef({
    objects, selectedIds, scale, onTransform,
  })
  latest.current = {
    objects, selectedIds, scale, onTransform,
  }

  const register = useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      elements.current.set(id, element)
      rulesByElement.set(element, rules.current.get(id) ?? DEFAULT_RULES)
    } else {
      elements.current.delete(id)
      rules.current.delete(id)
    }
    setRegistryVersion((version) => version + 1)
  }, [])

  const setRules = useCallback((id: string, next: Partial<ObjectRules>) => {
    const merged = { ...(rules.current.get(id) ?? DEFAULT_RULES), ...next }
    rules.current.set(id, merged)
    const element = elements.current.get(id)
    if (element) rulesByElement.set(element, merged)
    setRegistryVersion((version) => version + 1)
  }, [])

  const dragAfterSelect = useCallback((event: MouseEvent) => {
    const moveable = moveableRef.current
    if (!moveable) return

    let released = false
    document.addEventListener("mouseup", () => { released = true }, { once: true })
    moveable.waitToChangeTarget().then(() => {
      if (!released) moveable.dragStart(event)
    })
  }, [])

  // Shift keeps proportions and turns in steps; the mouse events carry it too
  useEffect(() => {
    const follow = (event: KeyboardEvent | MouseEvent) => setShiftHeld(event.shiftKey)
    const events = ["keydown", "keyup", "mousedown", "mousemove"] as const
    events.forEach((name) => document.addEventListener(name, follow, true))

    return () => events.forEach((name) => document.removeEventListener(name, follow, true))
  }, [])

  const [targets, setTargets] = useState<HTMLElement[]>([])
  const selectionKey = selectedIds.join(",")
  useLayoutEffect(() => {
    setTargets(selectedIds
      .map((id) => elements.current.get(id))
      .filter((element): element is HTMLElement => !!element))
  }, [selectionKey, registryVersion])

  const selectedRules = selectedIds.map((id) => rules.current.get(id) ?? DEFAULT_RULES)
  const selectedObjects = objects.filter((object) => selectedIds.includes(object.id))
  const resizable = selectedRules.length > 0 && selectedRules.every((rule) => rule.resizable)
  const rotatable = selectedRules.length > 0 && selectedRules.every((rule) => rule.rotatable)
  const square = selectedRules.some((rule) => rule.square)
  const keptInside = selectedObjects.some((object) => !object.canLeaveBounds)

  const frame = { width: bounds.width * scale, height: bounds.height * scale }
  const grid = getGridPositions(bounds.width, bounds.height)
  const gridLines = (positions: number[]) => (gridGuides ? positions.map((at) => at * scale) : [])
  const verticalGuidelines = [0, frame.width, ...gridLines(grid.x)]
  const horizontalGuidelines = [0, frame.height, ...gridLines(grid.y)]
  const elementGuidelines = objects
    .filter((object) => !selectedIds.includes(object.id))
    .map((object) => elements.current.get(object.id))
    .filter((element): element is HTMLElement => !!element)

  // The gesture moves the elements' inline styles; they are put back as React drew them
  // before the change is committed, so React applies it from where it left off
  const drawn = useRef(new Map<HTMLElement, string>())
  const remember = (targetList: Target[]) => {
    drawn.current = new Map(targetList.map((target) => [
      target as HTMLElement, target.style.cssText,
    ]))
  }
  const commit = (changes: Map<string, ObjectChange>) => {
    drawn.current.forEach((cssText, target) => {
      // eslint-disable-next-line no-param-reassign
      target.style.cssText = cssText
    })
    drawn.current.clear()
    store.setState({ live: {} })
    if (changes.size > 0) flushSync(() => latest.current.onTransform(changes))
  }

  const objectOf = (target: Target) => {
    const id = (target as HTMLElement).dataset.objectId
    const object = latest.current.objects.find((candidate) => candidate.id === id)

    return object && id ? { id, object } : null
  }

  // A gesture may start on a pressed object when it takes the press (not inside an unfilled
  // shape, not while its text is edited, not while Space pans)
  const mayStart = (inputEvent: MouseEvent | undefined, targetList: Target[]) => {
    if (getViewport().spaceHeld) return false
    if (inputEvent && pressMissesObject(inputEvent)) return false

    return !targetList.some((target) => rulesByElement.get(target)?.locked)
  }

  const moved = (event: OnDrag | undefined) => {
    const [x, y] = event?.beforeTranslate ?? [0, 0]

    return { x: x / latest.current.scale, y: y / latest.current.scale }
  }

  const movedChange = (event: OnDragEnd) => {
    const found = objectOf(event.target)
    if (!found || !event.lastEvent) return null
    const delta = moved(event.lastEvent)

    return [found.id, {
      x: round(found.object.x + delta.x), y: round(found.object.y + delta.y),
    }] as const
  }

  // The size from the drawn size's change, so the stored size keeps its precision
  const resizedRect = (event: OnResize, start: ResizeStart) => {
    const unit = latest.current.scale
    const delta = moved(event.drag)

    return {
      x: round(start.x + delta.x),
      y: round(start.y + delta.y),
      width: round(start.width + (event.width - start.drawnWidth) / unit),
      height: round(start.height + (event.height - start.drawnHeight) / unit),
    }
  }

  const resizeStart = (event: OnResizeStart) => {
    const found = objectOf(event.target)
    const minSize = rulesByElement.get(event.target)?.minSize ?? 1
    event.setMin([minSize * latest.current.scale, minSize * latest.current.scale])
    if (found) {
      // eslint-disable-next-line no-param-reassign
      event.datas.start = {
        ...found.object,
        drawnWidth: (event.target as HTMLElement).offsetWidth,
        drawnHeight: (event.target as HTMLElement).offsetHeight,
      }
    }
  }

  const resizedChange = (event: OnResizeEnd) => {
    const found = objectOf(event.target)
    if (!found || !event.lastEvent || !event.datas.start) return null

    return [found.id, resizedRect(event.lastEvent, event.datas.start)] as const
  }

  const rotatedChange = (event: OnRotateEnd) => {
    const found = objectOf(event.target)
    const last = event.lastEvent as OnRotate | undefined
    if (!found || !last) return null
    const delta = moved(last.drag)

    return [found.id, {
      x: round(found.object.x + delta.x),
      y: round(found.object.y + delta.y),
      rotation: normaliseAngle(last.rotate),
    }] as const
  }

  const showLive = (id: string, rect: Rect, rotation: number) => store.setState((state) => ({
    live: { ...state.live, [id]: { rect, rotation } },
  }))

  const changesOf = <E, >(events: E[], change: (event: E) => Entry | null) => (
    new Map(events.map(change).filter((entry): entry is Entry => !!entry))
  )

  const drag = useCallback((event: MouseEvent) => {
    moveableRef.current?.dragStart(event)
  }, [])

  const value = useMemo(() => ({
    register, setRules, dragAfterSelect, drag, store, bounds, scale,
  }), [register, setRules, dragAfterSelect, drag, store, bounds, scale])

  return (
    <LayerContext.Provider value={value}>
      {children}
      <Global styles={gestureCursorStyles} />
      <Moveable
        ref={moveableRef}
        target={targets.length === 1 ? targets[0] : targets}
        className="editor-moveable"
        flushSync={flushSync}
        // A press on an object takes the focus from a field, as anywhere else on the page
        preventDefault={false}
        zoom={1 / zoom}
        viewContainer={document.body}
        useResizeObserver
        useMutationObserver
        origin={false}
        // Presses inside a group's box reach the objects and the canvas under it
        passDragArea
        draggable
        resizable={resizable}
        keepRatio={square || shiftHeld}
        renderDirections={["nw", "n", "ne", "e", "se", "s", "sw", "w"]}
        rotatable={rotatable}
        rotationPosition="top"
        throttleRotate={shiftHeld ? ROTATION_STEP : 0}
        snappable
        snapContainer={targets[0]?.parentElement ?? null}
        snapThreshold={snapping ? SNAP_THRESHOLD_PX / zoom : 0}
        snapDirections={{
          left: true, top: true, right: true, bottom: true, center: true, middle: true,
        }}
        elementSnapDirections={{
          left: true, top: true, right: true, bottom: true, center: true, middle: true,
        }}
        snapDistFormat={(distance: number) => (
          formatDistance ? formatDistance(distance / scale) : distance
        )}
        verticalGuidelines={snapping ? verticalGuidelines : []}
        horizontalGuidelines={snapping ? horizontalGuidelines : []}
        elementGuidelines={snapping ? elementGuidelines : []}
        // Insets from the frame's edges, like CSS
        bounds={keptInside ? {
          left: 0, top: 0, right: 0, bottom: 0, position: "css",
        } : null}
        onDragStart={(event: OnDragStart) => {
          if (event.inputEvent?.shiftKey) return false
          if (!mayStart(event.inputEvent, [event.target])) return false
          remember([event.target])

          return true
        }}
        onDrag={(event: OnDrag) => {
          // eslint-disable-next-line no-param-reassign
          event.target.style.transform = event.transform
          const found = objectOf(event.target)
          if (found) {
            const delta = moved(event)
            showLive(found.id, {
              ...found.object, x: found.object.x + delta.x, y: found.object.y + delta.y,
            }, found.object.rotation)
          }
        }}
        onDragEnd={(event: OnDragEnd) => {
          commit(event.isDrag ? changesOf([event], movedChange) : new Map())
        }}
        onDragGroupStart={(event) => {
          if (event.inputEvent?.shiftKey || !mayStart(event.inputEvent, event.targets)) return false
          remember(event.targets)

          return true
        }}
        onDragGroup={(event) => event.events.forEach((child) => {
          // eslint-disable-next-line no-param-reassign
          child.target.style.transform = child.transform
        })}
        onDragGroupEnd={(event) => {
          commit(event.isDrag ? changesOf(event.events, movedChange) : new Map())
        }}
        onResizeStart={(event: OnResizeStart) => {
          if (!mayStart(undefined, [event.target])) return false
          remember([event.target])
          resizeStart(event)

          return true
        }}
        onResize={(event: OnResize) => {
          /* eslint-disable no-param-reassign */
          event.target.style.width = `${event.width}px`
          event.target.style.height = `${event.height}px`
          event.target.style.transform = event.drag.transform
          /* eslint-enable no-param-reassign */
          const found = objectOf(event.target)
          if (found && event.datas.start) {
            showLive(found.id, resizedRect(event, event.datas.start), found.object.rotation)
          }
        }}
        onResizeEnd={(event: OnResizeEnd) => {
          commit(event.isDrag ? changesOf([event], resizedChange) : new Map())
        }}
        onResizeGroupStart={(event) => {
          if (!mayStart(undefined, event.targets)) return false
          remember(event.targets)
          event.events.forEach(resizeStart)

          return true
        }}
        onResizeGroup={(event) => event.events.forEach((child) => {
          /* eslint-disable no-param-reassign */
          child.target.style.width = `${child.width}px`
          child.target.style.height = `${child.height}px`
          child.target.style.transform = child.drag.transform
          /* eslint-enable no-param-reassign */
          const found = objectOf(child.target)
          if (found && child.datas.start) {
            showLive(found.id, resizedRect(child, child.datas.start), found.object.rotation)
          }
        })}
        onResizeGroupEnd={(event) => {
          commit(event.isDrag ? changesOf(event.events, resizedChange) : new Map())
        }}
        onRotateStart={(event: OnRotateStart) => {
          if (!mayStart(undefined, [event.target])) return false
          remember([event.target])

          return true
        }}
        onRotate={(event: OnRotate) => {
          // eslint-disable-next-line no-param-reassign
          event.target.style.transform = event.drag.transform
          const found = objectOf(event.target)
          if (found) showLive(found.id, found.object, event.rotate)
        }}
        onRotateEnd={(event: OnRotateEnd) => {
          commit(event.isDrag ? changesOf([event], rotatedChange) : new Map())
        }}
        onRotateGroupStart={(event) => {
          if (!mayStart(undefined, event.targets)) return false
          remember(event.targets)

          return true
        }}
        onRotateGroup={(event) => event.events.forEach((child) => {
          // eslint-disable-next-line no-param-reassign
          child.target.style.transform = child.drag.transform
        })}
        onRotateGroupEnd={(event) => {
          commit(event.isDrag ? changesOf(event.events, rotatedChange) : new Map())
        }}
      />
    </LayerContext.Provider>
  )
}

/** The object layer around a part: its registry, bounds and scale. */
export const useObjectLayer = () => {
  const context = useContext(LayerContext)
  if (!context) throw new Error("Object layer hooks must be used inside an ObjectLayer")

  return context
}

/** Where an object is drawn while a gesture moves it, or null. */
export const useLiveObject = (id: string) => useStore(
  useObjectLayer().store,
  (state) => state.live[id] ?? null,
)

export default ObjectLayer
